import React, { createContext, useContext, useState, ReactNode } from 'react';
import { NodeType } from '../utils/nodeId';

/** Represents an item in the clipboard */
export interface ClipboardItem {
  kind: NodeType;
  id: number;
}

/** Clipboard state and actions */
export interface ClipboardState {
  mode: 'cut' | 'copy' | null;
  items: ClipboardItem[];
  copy: (items: ClipboardItem[]) => void;
  cut: (items: ClipboardItem[]) => void;
  clear: () => void;
}

const ClipboardContext = createContext<ClipboardState | undefined>(undefined);

/** Provider that wraps the app and exposes clipboard actions */
export const ClipboardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ClipboardState['mode']>(null);
  const [items, setItems] = useState<ClipboardItem[]>([]);

  const copy = (its: ClipboardItem[]) => {
    setMode('copy');
    setItems(its);
  };

  const cut = (its: ClipboardItem[]) => {
    setMode('cut');
    setItems(its);
  };

  const clear = () => {
    setMode(null);
    setItems([]);
  };

  return (
    <ClipboardContext.Provider value={{ mode, items, copy, cut, clear }}>
      {children}
    </ClipboardContext.Provider>
  );
};

/** Hook to consume clipboard context */
export function useClipboard(): ClipboardState {
  const ctx = useContext(ClipboardContext);
  if (!ctx) throw new Error('useClipboard must be used within a ClipboardProvider');
  return ctx;
} 